/**
 * AI-generated code, moderated by Lukasz Zygmanski
 */
import {isEntitled} from '@nursio-link/common/authorization';
import type {Entitlement} from '@nursio-link/common/model';

import {fallbackLanguage, isSupportedLanguage} from '@/i18nConfig';
import type {UserGroup} from '@/services/graphqlHooks';
import type {
  LocalizedReleaseNote,
  LocalizedReleaseNoteText,
  ReleaseNote,
  ReleaseNoteLanguage,
} from '@/shared/release-notes/types/releaseNotes';

const RELEASE_NOTES_SEEN_STATE_KEY_PREFIX = 'nursio:release-notes-seen-state';

type ReleaseNotesSeenState = {
  initialized: true;
  seenReleaseNoteIds: string[];
};

export function getVisibleReleaseNotes(
  releaseNotes: ReleaseNote[],
  groups: UserGroup[] = [],
  entitlements: Entitlement[] = []
) {
  return releaseNotes
    .filter(
      releaseNote =>
        isReleaseNoteVisibleForGroups(releaseNote, groups) &&
        isReleaseNoteVisibleForEntitlements(releaseNote, entitlements)
    )
    .sort(
      (first, second) =>
        new Date(second.publishedAt).getTime() -
        new Date(first.publishedAt).getTime()
    );
}

function isReleaseNoteVisibleForGroups(
  releaseNote: ReleaseNote,
  groups: UserGroup[]
) {
  return (
    !releaseNote.audiences.length ||
    releaseNote.audiences.some(group => groups.includes(group))
  );
}

function isReleaseNoteVisibleForEntitlements(
  releaseNote: ReleaseNote,
  entitlements: Entitlement[]
) {
  return (
    !releaseNote.entitlements.length ||
    releaseNote.entitlements.some(entitlement =>
      isEntitled(entitlements, entitlement)
    )
  );
}

export function hasReleaseNotesSeenState(userId?: string | null) {
  return Boolean(
    userId && localStorage.getItem(getSeenReleaseNotesStorageKey(userId))
  );
}

export function getModalReleaseNotes(releaseNotes: ReleaseNote[]) {
  return releaseNotes.filter(releaseNote => releaseNote.announce);
}

export function getSeenReleaseNoteIds(userId?: string | null) {
  return getReleaseNotesSeenState(userId)?.seenReleaseNoteIds || [];
}

export function initializeReleaseNotesSeenState(
  userId: string,
  releaseNoteIds: string[]
) {
  setReleaseNotesSeenState(userId, {
    initialized: true,
    seenReleaseNoteIds: Array.from(new Set(releaseNoteIds)),
  });
}

export function markVisibleReleaseNotesAsSeen(
  userId: string,
  visibleReleaseNoteIds: string[]
) {
  const currentState = getReleaseNotesSeenState(userId);
  const visibleReleaseNoteIdSet = new Set(visibleReleaseNoteIds);
  const nextReleaseNoteIds = Array.from(
    new Set([
      ...(currentState?.seenReleaseNoteIds || []),
      ...visibleReleaseNoteIds,
    ])
  ).filter(releaseNoteId => visibleReleaseNoteIdSet.has(releaseNoteId));

  setReleaseNotesSeenState(userId, {
    initialized: true,
    seenReleaseNoteIds: nextReleaseNoteIds,
  });
}

function getReleaseNotesSeenState(
  userId?: string | null
): ReleaseNotesSeenState | null {
  if (!userId) return null;

  const storedValue = localStorage.getItem(
    getSeenReleaseNotesStorageKey(userId)
  );
  if (!storedValue) return null;

  try {
    const parsedValue = JSON.parse(storedValue) as unknown;

    if (isReleaseNotesSeenState(parsedValue)) return parsedValue;
  } catch {
    return null;
  }

  return null;
}

export function getUnseenReleaseNotes(
  releaseNotes: ReleaseNote[],
  seenReleaseNoteIds: string[]
) {
  return releaseNotes.filter(
    releaseNote => !seenReleaseNoteIds.includes(releaseNote.id)
  );
}

export function getLocalizedReleaseNotes(
  releaseNotes: ReleaseNote[],
  language: string
): LocalizedReleaseNote[] {
  return releaseNotes.map(releaseNote => ({
    ...releaseNote,
    body: getLocalizedReleaseNoteText(releaseNote.body, language),
    summary: getLocalizedReleaseNoteText(releaseNote.summary, language),
    title: getLocalizedReleaseNoteText(releaseNote.title, language),
  }));
}

function getSeenReleaseNotesStorageKey(userId: string) {
  return `${RELEASE_NOTES_SEEN_STATE_KEY_PREFIX}:${userId}`;
}

function setReleaseNotesSeenState(
  userId: string,
  releaseNotesSeenState: ReleaseNotesSeenState
) {
  localStorage.setItem(
    getSeenReleaseNotesStorageKey(userId),
    JSON.stringify(releaseNotesSeenState)
  );
}

function isReleaseNotesSeenState(
  value: unknown
): value is ReleaseNotesSeenState {
  if (!value || typeof value !== 'object') return false;

  const releaseNotesSeenState = value as Partial<ReleaseNotesSeenState>;

  return (
    releaseNotesSeenState.initialized === true &&
    Array.isArray(releaseNotesSeenState.seenReleaseNoteIds) &&
    releaseNotesSeenState.seenReleaseNoteIds.every(
      releaseNoteId => typeof releaseNoteId === 'string'
    )
  );
}

function getLocalizedReleaseNoteText(
  text: LocalizedReleaseNoteText,
  language: string
) {
  const releaseNoteLanguage = getReleaseNoteLanguage(language);

  return text[releaseNoteLanguage] || text.en;
}

function getReleaseNoteLanguage(language: string): ReleaseNoteLanguage {
  const languageCode = language.split('-')[0];

  if (isSupportedLanguage(languageCode)) {
    return languageCode;
  }

  return fallbackLanguage;
}
